class LocalizationForm extends HTMLElement {
	constructor() {
		super();
		this.elements = {
			input: this.querySelector(
				'input[name="language_code"], input[name="country_code"]'
			),
			button: this.querySelector("button"),
			panel: this.querySelector("ul"),
		};
		this.elements.button.addEventListener(
			"click",
			this.openSelector.bind(this)
		);
		this.elements.button.addEventListener(
			"focusout",
			this.closeSelector.bind(this)
		);
		this.addEventListener("keyup", this.onContainerKeyUp.bind(this));

		this.querySelectorAll("a").forEach((item) =>
			item.addEventListener("click", this.onItemClick.bind(this))
		);
	}

	hidePanel() {
		this.elements.button.setAttribute("aria-expanded", "false");
		this.elements.panel.setAttribute("hidden", true);
	}

	onContainerKeyUp(event) {
		if (event.code.toUpperCase() !== "ESCAPE") return;

		this.hidePanel();
		this.elements.button.focus();
	}

	onItemClick(event) {
		event.preventDefault();
		const form = this.querySelector("form");
		this.elements.input.value = event.currentTarget.dataset.value;
		if (form) form.submit();
	}

	openSelector() {
		this.elements.button.focus();
		this.elements.panel.toggleAttribute("hidden");
		this.elements.button.setAttribute(
			"aria-expanded",
			(
				this.elements.button.getAttribute("aria-expanded") === "false"
			).toString()
		);
	}

	closeSelector(event) {
		const shouldClose =
			event.relatedTarget && event.relatedTarget.nodeName === "BUTTON";
		if (event.relatedTarget === null || shouldClose) {
			this.hidePanel();
		}
	}
}
customElements.define("localization-form", LocalizationForm);
class ComponentAccordion extends HTMLElement {
	constructor() {
		super();
	}

	connectedCallback() {
		this.addEventListener("click", this.handleClick.bind(this));
		this.refreshAccordion = this.refreshAccordion.bind(this);

		if (this.classList.contains("is-active")) {
			this.expandAccordion(this);
		}
	}

	disconnectedCallback() {
		this.removeEventListener("click", this.handleClick);
	}

	refreshAccordion(accordion) {
		const content = accordion.querySelector(".js-accordion-content");
		this.adjustHeight(content, accordion.classList.contains("is-active"));
	}

	handleClick(e) {
		const toggle = e.target.closest(".js-accordion-toggle");
		if (!toggle) return;

		const accordion = toggle.closest(".c-accordion");
		const closeSiblings = accordion.classList.contains("is-close-siblings");
		this.toggleAccordion(accordion, closeSiblings);
	}

	toggleAccordion(accordion, closeSiblings) {
		if (!accordion) return;

		const isActive = accordion.classList.contains("is-active");

		if (closeSiblings) {
			getSiblings(accordion).forEach((item) => {
				if (item.classList.contains("is-active")) this.collapseAccordion(item);
			});
		}

		isActive
			? this.collapseAccordion(accordion)
			: this.expandAccordion(accordion);
	}

	expandAccordion(accordion) {
		const content = accordion.querySelector(".js-accordion-content");
		if (!content) return;

		accordion.classList.add("is-active");
		accordion.setAttribute("aria-expanded", true);
		content.setAttribute("aria-hidden", false);

		this.adjustHeight(content, true);
	}

	collapseAccordion(accordion) {
		const content = accordion.querySelector(".js-accordion-content");
		if (!content) return;

		accordion.classList.remove("is-active");
		accordion.setAttribute("aria-expanded", false);
		content.setAttribute("aria-hidden", true);

		this.adjustHeight(content, false);
	}

	adjustHeight(wrapperEl, isOpening) {
		if (!wrapperEl) return;

		const clear = () => {
			wrapperEl.style.height = isOpening ? "auto" : "";
			wrapperEl.removeEventListener("transitionend", clear);
		};

		if (isOpening) {
			wrapperEl.style.height = "0";
			wrapperEl.offsetHeight;
			wrapperEl.style.height = wrapperEl.scrollHeight + "px";
		} else {
			wrapperEl.style.height = wrapperEl.scrollHeight + "px";
			wrapperEl.offsetHeight;
			wrapperEl.style.height = "0";
		}

		wrapperEl.addEventListener("transitionend", clear);
	}
}
customElements.define("c-accordion", ComponentAccordion);

class ComponentCarousel extends HTMLElement {
	constructor() {
		super();
		this.emblaNode = this.querySelector(".js-embla");
		this.viewportNode = this.emblaNode.querySelector(".js-embla__viewport");
		this.slideCardNodes = this.querySelectorAll(".c-carousel__slide");
		this.prevButtonNode = this.querySelector(".js-embla-button-prev");
		this.nextButtonNode = this.querySelector(".js-embla-button-next");
		this.dotsNode = this.emblaNode.querySelector(".js-embla__dots");
		this.thumbNode = this.querySelector(".js-embla-thumbs");

		this.emblaOptions = JSON.parse(
			this.dataset.emblaOptions.replace(/'/g, '"').trim()
		);
		this.emblaApi = null;

		//Plugin
		this.enableWheel = this.dataset.emblaWheel === "true";
		this.enableFade = this.dataset.emblaFade === "true";
		this.enableAutoHeight = this.dataset.emblaAutoHeight === "true";
		this.enableAutoplay = this.dataset.emblaAutoplay === "true";
		this.enableClickToSlide = this.dataset.emblaClickToSlide === "true";

		this.autoplayDuration =
			parseInt(this.dataset.emblaAutoplayInterval * 1000) || 3000;
		this.enableAutoScroll = this.dataset.emblaAutoScroll === "true";
		this.previousSelectedIndex = 0;
	}

	connectedCallback() {
		this.init();
	}

	init() {
		if (!window.EmblaCarousel || !this.emblaNode) return;

		const autoplay = EmblaCarouselAutoplay({
			playOnInit: this.enableAutoplay,
			delay: this.autoplayDuration,
			stopOnInteraction: false,
			stopOnMouseEnter: false,
		});

		const autoScrollSpeed = this.hasAttribute("data-embla-scroll-speed")
			? parseInt(this.dataset.emblaScrollSpeed)
			: 2;
		const autoScrollDelay = this.hasAttribute("data-embla-scroll-delay")
			? parseInt(this.dataset.emblaScrollDelay)
			: 0;
		const autoScrollStopOnMouseEnter = this.dataset.emblaScrollStopOnMouseEnter;

		const autoScrollDirection =
			this.dataset.emblaScrollBackward === "true" ? "backward" : "forward";

		const autoScroll = EmblaCarouselAutoScroll({
			playOnInit: this.enableAutoScroll,
			speed: autoScrollSpeed,
			startDelay: autoScrollDelay,
			direction: autoScrollDirection,
			stopOnInteraction: false,
			stopOnMouseEnter: autoScrollStopOnMouseEnter,
		});

		const plugins = [
			EmblaCarouselClassNames(),
			...(this.enableWheel ? [EmblaCarouselWheelGestures()] : []),
			...(this.enableAutoplay ? [autoplay] : []),
			...(this.enableFade ? [EmblaCarouselFade()] : []),
			...(this.enableAutoHeight ? [EmblaCarouselAutoHeight()] : []),
			...(this.enableAutoScroll ? [autoScroll] : []),
		];

		let options = this.emblaOptions;
		if (typeof options.align === "number") {
			options = {
				...options,
				align: () => {
					return options.align;
				},
			};
		}

		this.emblaApi = window.EmblaCarousel(this.viewportNode, options, plugins);

		if (this.prevButtonNode && this.nextButtonNode) {
			const removePrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(
				this.emblaApi,
				this.prevButtonNode,
				this.nextButtonNode
			);
			this.emblaApi.on("destroy", removePrevNextBtnsClickHandlers);
		}

		if (this.dotsNode) {
			const removeDotBtnsAndClickHandlers = this.addDotBtnsAndClickHandlers(
				this.emblaApi,
				this.dotsNode
			);

			this.emblaApi.on("destroy", removeDotBtnsAndClickHandlers);
		}

		if (this.enableClickToSlide) {
			const removeClickToSlideHandler = this.addClickToSlideHandler(
				this.emblaApi,
				this.slideCardNodes
			);
			this.emblaApi.on("destroy", removeClickToSlideHandler);
		}

		if (this.enableAutoplay) {
			this.emblaApi.on("autoplay:play", this.startAutoplay.bind(this));
			this.emblaApi.on("autoplay:stop", this.stopAutoplay.bind(this));
			this.emblaApi.on("select", this.autoplaySelect.bind(this));
			this.emblaApi.on("init", this.startAutoplay.bind(this));

			if (this.dotsNode) {
				this.emblaApi.on("scroll", this.onScrollAnimateDots.bind(this));
				this.emblaApi.on("settle", this.onScrollSettle.bind(this));
			}
		}

		if (this.thumbNode) {
			this.initializeThumbnailsNavigation();
		}
	}
	// Thumbnail navigation
	initializeThumbnailsNavigation() {
		this.viewportThumbNode = this.thumbNode.querySelector(
			".js-embla-thumbs-viewport"
		);

		const options = {
			align: "start",
			loop: true,
			breakpoints: {
				"(min-width: 1024px)": { axis: "y", align: "start" },
			},
		};

		this.emblaThumb = EmblaCarousel(this.viewportThumbNode, options, [
			EmblaCarouselWheelGestures(),
		]);

		this.addThumbBtnsClickHandlers(this.emblaApi, this.emblaThumb);
		this.toggleThumbBtnsActive(this.emblaApi, this.emblaThumb);

		const handleThumbResize = () => {
			const isExceedViewport = this.emblaThumb.scrollSnapList().length > 1;
			this.viewportThumbNode.classList.toggle(
				"is-justify-center",
				!isExceedViewport
			);
			this.emblaThumb.reInit({ watchDrag: isExceedViewport });
		};

		// justify center thumbnails when container does not exceed viewport
		handleThumbResize();
		this.emblaThumb.on("resize", handleThumbResize);
		// prevent overscrolling
		this.emblaThumb.on("scroll", (emblaApi) => {
			const {
				limit,
				target,
				location,
				offsetLocation,
				scrollTo,
				translate,
				scrollBody,
			} = emblaApi.internalEngine();

			let edge;

			if (limit.reachedMax(target.get())) edge = limit.max;
			if (limit.reachedMin(target.get())) edge = limit.min;

			if (!options.loop && edge !== undefined) {
				offsetLocation.set(edge);
				location.set(edge);
				target.set(edge);
				translate.to(edge);
				translate.toggleActive(false);
				scrollBody.useDuration(0).useFriction(0);
				scrollTo.distance(0, false);
			} else {
				translate.toggleActive(true);
			}
		});
	}

	addThumbBtnsClickHandlers(emblaApi, emblaThumb) {
		const slidesThumbs = emblaThumb.slideNodes();
		const scrollToIndex = slidesThumbs.map(
			(_, index) => () => emblaApi.scrollTo(index)
		);
		slidesThumbs.forEach((slideNode, index) => {
			slideNode.addEventListener("click", scrollToIndex[index], false);
		});

		this.removeThumbBtnsClickHandlers = () => {
			slidesThumbs.forEach((slideNode, index) => {
				slideNode.removeEventListener("click", scrollToIndex[index], false);
			});
		};
	}

	toggleThumbBtnsActive(emblaApi, emblaThumb) {
		const slidesThumbs = emblaThumb.slideNodes();
		const toggleThumbBtnsState = () => {
			emblaThumb.scrollTo(emblaApi.selectedScrollSnap());
			const previous = emblaApi.previousScrollSnap();
			const selected = emblaApi.selectedScrollSnap();
			slidesThumbs[previous].classList.remove("is-selected");
			slidesThumbs[selected].classList.add("is-selected");
		};

		emblaApi.on("select", toggleThumbBtnsState);
		emblaThumb.on("init", toggleThumbBtnsState);

		this.removeToggleThumbBtnsActive = () => {
			const selected = emblaApi.selectedScrollSnap();
			slidesThumbs[selected].classList.remove("is-selected");
		};
	}

	// Prev & Next Buttons
	addTogglePrevNextBtnsActive = (emblaApi, prevBtn, nextBtn) => {
		const togglePrevNextBtnsState = () => {
			if (emblaApi.canScrollPrev()) prevBtn.removeAttribute("disabled");
			else prevBtn.setAttribute("disabled", "disabled");

			if (emblaApi.canScrollNext()) nextBtn.removeAttribute("disabled");
			else nextBtn.setAttribute("disabled", "disabled");
		};

		emblaApi
			.on("select", togglePrevNextBtnsState)
			.on("init", togglePrevNextBtnsState)
			.on("reInit", togglePrevNextBtnsState);

		return () => {
			prevBtn.removeAttribute("disabled");
			nextBtn.removeAttribute("disabled");
		};
	};

	addPrevNextBtnsClickHandlers = (emblaApi, prevBtn, nextBtn) => {
		const scrollPrev = () => {
			emblaApi.scrollPrev();
		};

		const scrollNext = () => {
			emblaApi.scrollNext();
		};
		prevBtn.addEventListener("click", scrollPrev, false);
		nextBtn.addEventListener("click", scrollNext, false);

		const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(
			emblaApi,
			prevBtn,
			nextBtn
		);

		return () => {
			removeTogglePrevNextBtnsActive();
			prevBtn.removeEventListener("click", scrollPrev, false);
			nextBtn.removeEventListener("click", scrollNext, false);
		};
	};

	addDotBtnsAndClickHandlers = (emblaApi, dotsNode) => {
		let dotNodes = [];

		const addDotBtnsWithClickHandlers = () => {
			dotsNode.innerHTML = emblaApi
				.scrollSnapList()
				.map(
					(_, index) =>
						`<li role="presentation" class="c-carousel__dot" style="--item-index: ${index};"><button type="button" role="tab" class="c-carousel__dot__button increase-target-size"></button></li>`
				)
				.join("");

			const scrollTo = (index) => {
				emblaApi.scrollTo(index);
			};

			dotNodes = Array.from(dotsNode.querySelectorAll(".c-carousel__dot"));

			dotNodes.forEach((dotNode, index) => {
				const button = dotNode.querySelector(".c-carousel__dot__button");
				button.addEventListener("click", () => scrollTo(index), false);
			});
		};

		const toggleDotBtnsActive = () => {
			const previous = emblaApi.previousScrollSnap();
			const selected = emblaApi.selectedScrollSnap();
			dotNodes[previous].classList.remove("c-carousel__dot--selected");
			dotNodes[selected].classList.add("c-carousel__dot--selected");
		};

		emblaApi
			.on("init", addDotBtnsWithClickHandlers)
			.on("reInit", addDotBtnsWithClickHandlers)
			.on("init", toggleDotBtnsActive)
			.on("reInit", toggleDotBtnsActive)
			.on("select", toggleDotBtnsActive);

		return () => {
			dotsNode.innerHTML = "";
		};
	};

	addClickToSlideHandler = (emblaApi, slideCards) => {
		const scrollTo = (index) => emblaApi.scrollTo(index);

		slideCards.forEach((card, index) =>
			card.addEventListener("click", () => scrollTo(index), false)
		);

		return () => {
			slideCards.forEach((card, index) =>
				card.removeEventListener("click", () => scrollTo(index), false)
			);
		};
	};

	autoplaySelect = (e) => {
		const autoplay = e?.plugins()?.autoplay;
		const resetOrStop =
			autoplay.options.stopOnInteraction === false
				? autoplay.reset
				: autoplay.stop;

		resetOrStop();
	};

	startAutoplay() {
		this.classList.remove("is-autoplay-paused");
		this.classList.add("is-autoplay-playing");
	}

	stopAutoplay() {
		this.classList.remove("is-autoplay-playing");
		this.classList.add("is-autoplay-paused");
	}

	onScrollAnimateDots(e) {
		const slideCount = e.slideNodes().length;
		if (slideCount === 1) return;

		const scrollProgress = e.scrollProgress();
		const totalProgress = scrollProgress * (slideCount - 1);

		const normalizeProgress = ((scrollProgress % 1) + 1) % 1;
		const progress = normalizeProgress * slideCount;
		let autoplayProgress = progress;

		if (slideCount === 2) {
			autoplayProgress = Math.max(0, Math.min(1, totalProgress));
		} else {
			if (progress < 0) {
				autoplayProgress = 0;
			} else if (progress > slideCount - 1) {
				const excess = progress - (slideCount - 1);
				autoplayProgress = (slideCount - 1) * (1 - excess); // reverse direction
			}
		}

		this.style.setProperty("--autoplay-progress", autoplayProgress.toFixed(6));
	}

	onScrollSettle(e) {
		const selectedIndex = e.selectedScrollSnap();
		this.previousSelectedIndex = selectedIndex;
		this.style.setProperty("--autoplay-progress", selectedIndex);
	}

	disconnectedCallback() {
		if (this.emblaApi) {
			this.emblaApi.destroy();
		}
	}
}
customElements.define("c-carousel", ComponentCarousel);

class GlobalHeader {
	constructor() {
		this.gHeader = document.querySelector(".g-header");
		this.jsMobileMenuToggle = document.querySelector(".js-mobile-menu-toggle");
		this.SCROLL_THRESHOLD = 200;
		this.hoverTimeout = null;
	}

	init() {
		if (!this.gHeader) return;
		this.initMegaMenu();
		this.setupScrollListener();
		this.setupMobileInteractions();
	}

	setupScrollListener() {
		// this.handleHeaderScroll();
		window.addEventListener("scroll", this.handleHeaderScroll.bind(this));
	}

	handleHeaderScroll() {
		this.gHeader.classList.toggle(
			"is-shrink",
			window.scrollY >= this.SCROLL_THRESHOLD
		);
	}

	setupMobileInteractions() {
		on("body", "click", ".js-mobile-menu-toggle", (e) => {
			root.classList.toggle("mobile-menu-is-active");
		});

		window.addEventListener("click", this.handleOutsideClick.bind(this));
	}

	handleTabletSetup() {
		// Manage attribute for transition prevention on desktop (vs.isTabletDown logic)
		const toggles = document.querySelectorAll("[data-megamenu-toggle]");
		if (vs.isTabletScreen) {
			toggles.forEach((el) => {
				el.dataset.pageTransition = "none";
			});
		} else {
			toggles.forEach((el) => {
				el.removeAttribute("data-page-transition");
			});
		}
	}

	handleOutsideClick(e) {
		if (root.classList.contains("mobile-menu-is-active")) {
			const target = document.querySelector(".g-mobile-menu");
			const withinBoundaries =
				e.composedPath().includes(target) ||
				e.composedPath().includes(this.jsMobileMenuToggle);

			if (!withinBoundaries) {
				root.classList.remove("mobile-menu-is-active");
			}
		}
	}

	initMegaMenu() {
		this.handleTabletSetup();
		window.addEventListener("resize", this.handleTabletSetup.bind(this));

		this.gHeader.addEventListener(
			"mouseover",
			this.handleMegaMenuHover.bind(this)
		);

		this.gHeader.addEventListener(
			"mouseout",
			this.handleMegaMenuLeave.bind(this)
		);

		on(
			"body",
			"click",
			".js-megamenu-close, .js-g-overlay",
			this.megaMenuCloseAll.bind(this)
		);
		on("body", "click", ".js-megamenu-back", () =>
			this.megaMenuCloseAll(false)
		); // Back button
		on("body", "keydown", "body", this.handleKeydown.bind(this)); // Escape key
	}

	handleMegaMenuHover(e) {
		const trigger = e.target.closest("[data-megamenu-toggle]");

		if (trigger || e.target.closest("[data-megamenu]")) {
			clearTimeout(this.hoverTimeout);
			if (trigger) {
				this.megaMenuOpen(trigger);
			}
		}
	}

	handleMegaMenuLeave() {
		this.hoverTimeout = setTimeout(() => {
			this.megaMenuCloseAll();
		}, 300);
	}

	handleKeydown(e) {
		if (e.key === "Escape") {
			this.megaMenuCloseAll();
		}
	}

	megaMenuOpen(trigger) {
		const targetName = trigger.dataset.megamenuToggle;
		const targetMenus = document.querySelector(
			`[data-megamenu="${targetName}"]`
		);

		if (targetMenus.length === 0) return;
		this.megaMenuCloseAll();
		setAriaAttributes(targetMenus, true);
		targetMenus.classList.add("is-active");
		targetMenus.style.height = targetMenus.scrollHeight + "px";
		root.classList.add("is-megamenu-active");
	}

	megaMenuCloseAll(shouldEnableScroll = true) {
		const activeMenus = document.querySelectorAll(
			".g-header__megamenu__item.is-active"
		);

		root.classList.remove("is-megamenu-active");

		activeMenus.forEach((menu) => {
			menu.classList.remove("is-active");
			menu.style.height = "0px";
			setAriaAttributes(menu, false);
		});

		// 3. Enable scroll if needed (used for back buttons in your original code)
		if (shouldEnableScroll) {
			scrollEnable();
		}
	}
}

class ProductDetailPage {
	constructor() {
		this.main = document.querySelector("#main");
		this.productHero = document.querySelector(".product-hero");
		this.stickyAddToCart = document.querySelector(".c-sticky-add-to-cart");

		// Cache frequently accessed values
		this.mainHeight = this.main?.getBoundingClientRect().height || 0;
		this.handleScroll = this.handleScroll.bind(this);
	}

	init() {
		if (!this.productHero) return;

		this.productHeroHeight = this.productHero.getBoundingClientRect().height;
		this.setupScrollBehavior();
		this.setupLightboxes();
		this.setupDetailToggles();
	}

	setupScrollBehavior() {
		if (this.mainHeight < window.innerHeight * 2) {
			this.stickyAddToCart?.classList.add("relative");
		} else {
			window.addEventListener("scroll", this.handleScroll, { passive: true });
		}
	}

	handleScroll() {
		const scrollY = window.scrollY;

		if (vs.isMobileScreen) {
			this.handleMobileScroll(scrollY);
		} else {
			this.handleDesktopScroll(scrollY);
		}
	}

	handleMobileScroll(scrollY) {
		const threshold = (this.mainHeight - this.productHeroHeight) * 0.6;
		this.stickyAddToCart?.classList.toggle("is-show", scrollY > threshold);
	}

	handleDesktopScroll(scrollY) {
		const stickyHeight =
			this.stickyAddToCart?.getBoundingClientRect().height || 0;
		const showThreshold =
			this.productHeroHeight + stickyHeight - window.innerHeight;

		this.stickyAddToCart?.classList.toggle("is-show", scrollY > showThreshold);

		// Handle product info switch
		if (this.productHeroHeight > window.innerHeight) {
			this.productHero.classList.toggle(
				"product-info-switch",
				scrollY < window.innerHeight
			);
		}

		// Handle review badge state
		this.productHero.classList.toggle(
			"review-badge-simple-state",
			scrollY >= window.innerHeight / 2
		);
	}

	setupLightboxes() {
		const closeLightbox = (className) => {
			root.classList.remove(className, "wheel-disable");
		};

		// Close ingredients lightbox
		on(".ingredients-lightbox", "click", ".c-lightbox__button-close", () => {
			closeLightbox("lightbox-open--ingredients");
		});

		// Close product reviews lightbox
		on(
			".product-reviews-lightbox",
			"click",
			".c-lightbox__button-close",
			() => {
				closeLightbox("lightbox-open--product-reviews");
			}
		);

		// Open product reviews
		on("body", "click", ".js-product-reviews-open", () => {
			root.classList.add("lightbox-open--product-reviews");
		});

		// ESC key handler
		document.addEventListener("keydown", (e) => {
			if (e.key === "Escape") {
				closeLightbox("lightbox-open--ingredients");
				closeLightbox("lightbox-open--product-reviews");
			}
		});
	}

	setupDetailToggles() {
		const buttons = document.querySelectorAll(".js-button-plus-toggle");

		buttons.forEach((button) => {
			button.addEventListener("click", () => {
				console.log(
					"🚀 ~ ProductDetailPage ~ setupDetailToggles ~ click:",
					"click"
				);
				const buttonType = button.dataset.buttonType;

				// Special case: ingredients opens lightbox
				if (buttonType === "ingredients") {
					root.classList.add("lightbox-open--ingredients");
					return;
				}

				this.toggleDetailContent(button);
			});
		});
	}

	toggleDetailContent(targetButton) {
		const buttonType = targetButton.dataset.buttonType;
		const targetContent = document.querySelector(
			`.product-hero__details__content--${buttonType}`
		);

		if (!targetContent) return;

		const isActive = targetButton.classList.contains("is-open");
		getSiblings(targetButton).forEach((siblingButton) => {
			if (siblingButton.classList.contains("is-open")) {
				const siblingButtonType = siblingButton.dataset.buttonType;
				const siblingContent = document.querySelector(
					`.product-hero__details__content--${siblingButtonType}`
				);

				if (siblingContent) {
					this.collapseDetail(siblingButton, siblingContent);
				}
			}
		});

		if (isActive) {
			this.collapseDetail(targetButton, targetContent);
		} else {
			this.expandDetail(targetButton, targetContent);
		}
	}

	expandDetail(targetButton, targetContent) {
		if (!targetButton || !targetContent) return;

		targetContent.classList.add("is-open");
		this.adjustDetailHeight(targetContent, true);
		requestAnimationFrame(() => {
			targetButton.classList.add("is-open");
		});
	}

	collapseDetail(targetButton, targetContent) {
		if (!targetButton || !targetContent) return;

		targetContent.classList.remove("is-open");
		this.adjustDetailHeight(targetContent, false);

		requestAnimationFrame(() => {
			targetButton.classList.remove("is-open");
		});
	}

	adjustDetailHeight(wrapperEl, isOpening) {
		if (!wrapperEl) return;

		const clear = () => {
			wrapperEl.style.height = isOpening ? "auto" : "";
			wrapperEl.removeEventListener("transitionend", clear);
		};

		if (isOpening) {
			wrapperEl.style.height = "0";
			wrapperEl.offsetHeight; // Force reflow
			wrapperEl.style.height = wrapperEl.scrollHeight + "px";
		} else {
			wrapperEl.style.height = wrapperEl.scrollHeight + "px";
			wrapperEl.offsetHeight; // Force reflow
			wrapperEl.style.height = "0";
		}

		wrapperEl.addEventListener("transitionend", clear);
	}
	destroy() {
		window.removeEventListener("scroll", this.handleScroll);
	}
}

class ProductGalleryDesktopNav {
	constructor() {
		const productHeroNode = document.querySelector(".product-hero");
		this.rootNode = productHeroNode;
		this.imageNodes = this.rootNode.querySelectorAll(
			".product-hero__images .c-carousel__slide"
		);
		this.dotsContainer = this.rootNode.querySelector(
			".product-hero__gallery__dots"
		);
		this.dots = [];
		this.observer = null;

		// Check if the screen is desktop size
		if (window.matchMedia("(min-width: 1025px)").matches) {
			this.init();
		}
	}

	init() {
		// this.prepareDOM();
		this.initDots();
		this.initIntersectionObserver();
		this.addClickListeners();
	}

	prepareDOM() {
		this.imageNodes.forEach((node, index) => {
			node.id = `product-slide-${index}`;
		});

		const emblaViewport = this.rootNode.querySelector(".product-hero__images");
		if (emblaViewport) {
			emblaViewport.classList.add("is-desktop-scroll");
		}
	}

	initDots() {
		this.dotsContainer.innerHTML = this.dotGenerator();
		this.dots = Array.from(
			this.dotsContainer.querySelectorAll(".product-hero__gallery__dot")
		);
	}

	dotGenerator() {
		let dotHtml = "";
		this.imageNodes.forEach((node, index) => {
			const targetId = `product-slide-${index}`;
			dotHtml += `<button class="product-hero__gallery__dot increase-target-size" type="button" data-target-id="${targetId}" data-index="${index}"></button>`;
		});
		return dotHtml;
	}

	addClickListeners() {
		this.dots.forEach((dot) => {
			dot.addEventListener("click", (event) => {
				const targetId = event.currentTarget.dataset.targetId;
				const targetElement = document.getElementById(targetId);

				if (targetElement) {
					targetElement.scrollIntoView({ behavior: "smooth" });
				}
			});
		});
	}

	initIntersectionObserver() {
		const observerOptions = {
			root: null,
			rootMargin: "0px 0px -50% 0px",
			threshold: 0,
		};

		// Callback function for when images enter/exit the view
		const observerCallback = (entries) => {
			entries.forEach((entry) => {
				const slideIndex = entry.target.dataset.index;
				const dot = this.dots.find((d) => d.dataset.index === slideIndex);

				if (dot) {
					if (entry.isIntersecting) {
						// When the image enters the viewport, activate the corresponding dot
						this.updateActiveDot(dot);
					} else {
						// Optional: remove active state if it scrolls completely out
						dot.classList.remove("is-active");
					}
				}
			});
		};

		this.observer = new IntersectionObserver(observerCallback, observerOptions);

		// Observe each image slide
		this.imageNodes.forEach((node, index) => {
			node.dataset.index = index; // Add index data for observer callback
			this.observer.observe(node);
		});
	}

	updateActiveDot(activeDot) {
		// Remove active class from all dots
		this.dots.forEach((dot) => dot.classList.remove("is-active"));
		// Add active class to the intersecting dot
		activeDot.classList.add("is-active");
	}
}
class ProductImageLeftRow extends HTMLElement {
	constructor() {
		super();
		this.embla = this.querySelector(".c-carousel");
		this.observer = null;
		this.throttleTimeout = null;
	}

	init() {
		if (!this.embla || !this.embla.emblaApi) {
			console.warn("Embla instance not found");
			return;
		}

		// Setup IntersectionObserver only once
		if (!this.observer) {
			this.observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							this.throttledScroll();
						}
					});
				},
				{
					root: null,
					threshold: [0.1],
				}
			);

			this.observer.observe(this.embla);
		}
	}

	throttledScroll() {
		if (this.throttleTimeout) return;

		this.throttleTimeout = setTimeout(() => {
			this.throttleTimeout = null;

			const emblaApi = this.embla.emblaApi;
			if (!emblaApi) return;

			const selectedIndex = emblaApi.selectedScrollSnap();
			const lastIndex = emblaApi.scrollSnapList().length - 1;

			if (selectedIndex === lastIndex) {
				emblaApi.scrollTo(0, { duration: 60 });
			} else {
				emblaApi.scrollNext({ duration: selectedIndex == 0 ? 10 : 60 });
			}
		}, 1000);
	}

	connectedCallback() {
		this.init();
	}

	disconnectedCallback() {
		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}
		if (this.throttleTimeout) {
			clearTimeout(this.throttleTimeout);
			this.throttleTimeout = null;
		}
	}
}
customElements.define("product-image-left-row", ProductImageLeftRow);
class PdpEditorialComponent extends HTMLElement {
	constructor() {
		super();
		this.lightBox = this.nextElementSibling;
		this.imageSlider = this.lightBox.querySelector(".image-slider");
		this.imageSliderList = this.lightBox.querySelector(".image-slider-list");
		this.lightBoxIsOpen = false;
		this.wheelNum = 0;
		this.initialScrollPerentage = 50;
		this.lightBox
			.querySelectorAll(".c-pdp-editorial__image-slider__list li")
			.forEach((item, index) => {
				if (index >= 1) {
					item.style.transitionDelay = `${(index * 0.2).toFixed(2)}s`;
				}
			});

		const imageSliderPreview = this.querySelector(
			".c-pdp-editorial__image-slider__list.image-preview"
		);

		const lightBoxTrigger = this.querySelector(".lightbox-trigger");
		const lightBoxButtonClose = this.lightBox.querySelector(
			".c-lightbox__button-close"
		);

		const openLightbox = () => {
			this.lightBoxIsOpen = true;
			root.classList.add("lightbox-open--pdp-editorial");
		};

		imageSliderPreview.addEventListener("click", openLightbox, false);
		lightBoxTrigger.addEventListener("click", openLightbox, false);
		lightBoxButtonClose.addEventListener(
			"click",
			this.closeLightBox.bind(this),
			false
		);

		on("body", "click", ".ingredients-trigger", (e) => {
			root.classList.remove("lightbox-open--pdp-editorial");
			root.classList.add("lightbox-open--ingredients", "wheel-disable");
		});

		window.addEventListener("wheel", this.handleScrollUpdate.bind(this), false);
		window.addEventListener(
			"keydown",
			(e) => {
				if (e.key == "Escape") {
					this.closeLightBox();
				}
			},
			false
		);
	}

	closeLightBox(e) {
		this.lightBoxIsOpen = false;
		root.classList.remove("lightbox-open--pdp-editorial");
		setTimeout(() => {
			this.lightBox.style.transform = null;
			this.imageSliderList.style.transform = `translate3d(${this.initialScrollPerentage}%, 0, 0)`;
			this.wheelNum = 0;
		}, 500);
	}

	handleScrollUpdate(e) {
		if (!this.lightBoxIsOpen || root.classList.contains("wheel-disable"))
			return;

		if (e.deltaY < 0) {
			this.wheelNum++;
		} else if (e.deltaY > 0) {
			this.wheelNum--;
		}
		const speed = 3000;
		let sliderScrollPercentage =
			(this.wheelNum / this.imageSlider.offsetWidth) * speed;

		if (sliderScrollPercentage >= 0) {
			this.wheelNum = 0;
			return;
		}

		if (sliderScrollPercentage <= -50) {
			let lightBoxScrollUp =
				this.initialScrollPerentage + sliderScrollPercentage;
			this.lightBox.style.transform = `translate3d(0, ${lightBoxScrollUp}%, 0)`;
		} else {
			this.imageSliderList.style.transform = `translate3d(${
				this.initialScrollPerentage + sliderScrollPercentage
			}%, 0, 0)`;
		}

		if (sliderScrollPercentage <= -150) {
			this.closeLightBox();
		}
	}
}
customElements.define("pdp-editorial", PdpEditorialComponent);
class EditorialComponent extends HTMLElement {
	constructor() {
		super();
		this.init();
		window.addEventListener(
			"scroll",
			this.handleScrollUpdate.bind(this),
			false
		);

		const backgroundMedia = this.querySelectorAll(
			".js-background-media .js-media-item"
		);

		let distanceSum = 0;
		let prevEvent;
		const DISTANCE_THRESHOLD = 12;
		const SPEED_THRESHOLD = 110;

		const handleBackgroundMediaChange = (e) => {
			let currentEvent = e;

			distanceSum++;
			if (prevEvent && currentEvent) {
				let movementX = Math.abs(currentEvent.screenX - prevEvent.screenX);
				let movementY = Math.abs(currentEvent.screenY - prevEvent.screenY);
				let movement = Math.sqrt(movementX * movementX + movementY * movementY);
				//speed=movement/100ms= movement/0.1s= 10*movement/s
				let speed = 10 * movement; //current speed

				if (speed > SPEED_THRESHOLD && distanceSum > DISTANCE_THRESHOLD) {
					const randomItem = getRandomInt(0, backgroundMedia.length - 1);

					backgroundMedia.forEach((item, index) => {
						if (index === randomItem) {
							item.classList.remove("is-fade-out");
						} else {
							item.classList.add("is-fade-out");
						}
					});

					distanceSum = 0;
				}
			}

			prevEvent = currentEvent;
		};

		this.addEventListener("mousemove", handleBackgroundMediaChange);
	}

	init() {
		if (vs.isTabletScreen === true) return;

		const distanceToTop = this.getBoundingClientRect().top;
		const eleRootElement = this.parentElement;
		const eleRootElementHeight = eleRootElement.getBoundingClientRect().height;

		if (distanceToTop <= 1) {
			eleRootElement.classList.add("is-sticky-top");
		}

		if (this.dataset.heightSize === "2") {
			eleRootElement.style.position = "sticky";
			eleRootElement.style.top = `-${
				this.getBoundingClientRect().height / 2
			}px`;
		}

		if (eleRootElementHeight > innerHeight) {
			eleRootElement.style.position = "sticky";
			eleRootElement.style.top = `${innerHeight - eleRootElementHeight}px`;
		}
	}

	handleScrollUpdate() {
		if (vs.isTabletScreen === true) return;
		const jsTextSlideUp = this.querySelectorAll(".js-text-slide-up");
		const distanceToTop = this.getBoundingClientRect().top;
		const target = this.parentElement;
		const scrollingElement = target.nextElementSibling;

		if (distanceToTop <= 0) {
			target.classList.add("is-sticky-top");
		} else {
			target.classList.remove("is-sticky-top");
			target.style.filter = null;
		}

		const scrollingEffect = (scrollPercentage) => {
			jsTextSlideUp.forEach((item) => {
				item.style.transform = `translate3d(0, -${(
					scrollPercentage * 100
				).toFixed(1)}%, 0)`;
			});

			const opacityValue = 1 - scrollPercentage * 1.2;
			const blurValue = scrollPercentage * 10;

			target.querySelectorAll(".c-image-main img").forEach((image) => {
				image.style.opacity = `${opacityValue.toFixed(2)}`;
				image.style.animation = "none";
			});

			target.querySelectorAll(".c-image-layer img").forEach((image) => {
				image.style.filter = `blur(${blurValue.toFixed(1)}px)`;
			});
		};

		if (
			target.classList.contains("is-sticky-top") &&
			scrollingElement &&
			!scrollingElement.classList.contains("is-sticky-top")
		) {
			const scrollPercentage =
				(target.offsetHeight - scrollingElement.getBoundingClientRect().top) /
				target.offsetHeight;

			if (this.dataset.heightSize === "2") {
				if (scrollPercentage >= 0.5) {
					const newScrollPercentage = scrollPercentage - 0.5;
					scrollingEffect(newScrollPercentage * 2);
					return;
				}
				return;
			}
			scrollingEffect(scrollPercentage);
		}
	}
}
customElements.define("editorial-component", EditorialComponent);

class ImageRandomizeComponent extends HTMLElement {
	constructor() {
		super();
		this.slides = this.querySelectorAll(".object-fit");

		if (!this.slides) return;
		this.speed = this.dataset.speed;
		this.action();
		this.stopStart();
	}

	generateRandom(maxLimit) {
		let rand = Math.random() * maxLimit;
		rand = Math.floor(rand);

		return rand;
	}

	_slideTo(slide) {
		let currentSlide = this.slides[slide];
		currentSlide.style.opacity = 1;

		for (let i = 0; i < this.slides.length; i++) {
			let slide = this.slides[i];
			if (slide !== currentSlide) {
				slide.style.opacity = 0;
			}
		}
	}

	action() {
		let self = this;
		const speed = self.speed * 1000;

		self.timer = setInterval(function () {
			const slide = self.generateRandom(self.slides.length);
			self._slideTo(slide);
		}, speed);
	}

	stopStart() {
		let self = this;
		this.addEventListener(
			"mouseover",
			function () {
				clearInterval(self.timer);
				self.timer = null;
			},
			false
		);
		self.addEventListener(
			"mouseout",
			function () {
				self.action();
			},
			false
		);
	}
}
customElements.define("c-img-randomize", ImageRandomizeComponent);

const initBlogIndex = () => {
	if (document.querySelector(".blog-pagination-next")) {
		let infiniteScroll = new InfiniteScroll(".blog-load-more", {
			path: ".blog-pagination-next",
			append: ".blog-post",
			button: ".blog-load-more-trigger",
			scrollThreshold: false,
			history: false,
		});

		infiniteScroll.on("last", (body, path) => {
			let loadMoreTrigger = document.querySelector(".blog-load-more-trigger");

			loadMoreTrigger.style.display = "block";
			loadMoreTrigger.disabled = true;
			loadMoreTrigger.innerHTML = "You’ve reached the end";
		});
	}
};

const initAccount = () => {
	// account gate
	const gateWrapper = document.querySelector(".account-gate-wrapper");
	const gateContent = document.querySelector(".account-gate");
	const resetPasswordTrigger = document.querySelector(
		".reset-password-trigger"
	);
	const resetPasswordCancel = document.querySelector(".reset-password-cancel");

	if (gateWrapper) {
		resetPasswordTrigger.addEventListener("click", (event) => {
			event.preventDefault();

			// update URL with hash
			history.pushState(null, null, "#recover");

			// assign active state
			root.classList.add("reset-password-active");

			// update wrapper height to fit account reset form
			gateWrapper.style.height =
				document.querySelector(".account-reset").offsetHeight;

			// focus on email field
			document.querySelector("#account-recover-email").focus();
		});

		if (resetPasswordCancel) {
			resetPasswordCancel.addEventListener("click", (event) => {
				event.preventDefault();

				// remove has from URL
				history.pushState(null, null, " ");

				// remove active state from root
				root.classList.remove("reset-password-active");

				// update wrapper height to fit account reset form
				gateWrapper.style.height = gateContent.offsetHeight;
			});
		}

		// trigger password recovery form if url contains the hash
		if (location.hash == "#recover") {
			resetPasswordTrigger.click();
		}

		// set initial height
		gateWrapper.style.height = gateContent.offsetHeight;
	}
};

const initAboutPage = () => {
	const items = document.querySelectorAll(".section-product .product-item");
	const { length } = items;

	if (!length) return;

	const randomIndex = length === 1 ? 0 : getRandomInt(0, length - 1);
	items[randomIndex].classList.remove("is-hidden");
};

const initContactPage = () => {
	if (vs.isMobileScreen) {
		return;
	}

	const handleScroll = () => {
		const scrollY = this.scrollY * 0.5;
		const MAX_SCROLL_VALUE = 226;
		if (scrollY >= MAX_SCROLL_VALUE) {
			return;
		}

		document.querySelector(
			".sticky-newsletter"
		).style.transform = `translate3d(0, -${scrollY}px, 0)`;
	};

	window.addEventListener("scroll", handleScroll, false);
};

const headerInstance = new GlobalHeader();
headerInstance.init();

const isTemplatePage = (templateName) =>
	Array.from(root.classList).some((cls) => cls.startsWith(templateName));

if (isTemplatePage("template-product")) {
	const pdpInstance = new ProductDetailPage();
	const pdpGalleryInstance = new ProductGalleryDesktopNav();
	pdpInstance.init();
	pdpGalleryInstance.init();
}
if (isTemplatePage("template-blog-index")) {
	initBlogIndex();
}

if (isTemplatePage("template-account")) {
	initAccount();
}

if (isTemplatePage("template-page-about")) {
	initAboutPage();
}

if (isTemplatePage("template-page-contact")) {
	initContactPage();
}
if (isTemplatePage("template-page-shipping-and-returns")) {
	initShippingPage();
}

// make visible .avoid-style-flash elements
setTimeout(() => {
	document.querySelectorAll(".avoid-style-flash").forEach((el) => {
		el.style.visibility = "visible";
	});
}, 400);
